import {
  finishAppleTransaction,
  getAppleCurrentEntitlements,
  purchaseAppleProduct,
  syncApplePurchases
} from './appleStoreKit.js';

function enabledValue() {
  return import.meta.env?.VITE_APPLE_IAP_ENABLED;
}

export function isAppleIapEnabled(value = enabledValue()) {
  return value === 'true';
}

async function clientOrThrow(client) {
  if (!client) ({supabase: client} = await import('../supabase/client.js'));
  if (!client) throw new Error('Supabase is not configured.');
  return client;
}

function firstRow(data) {
  return Array.isArray(data) ? data[0] : data;
}

export async function getApplePurchaseEligibility(client, {issueToken = false} = {}) {
  const resolvedClient = await clientOrThrow(client);
  const {data, error} = await resolvedClient.rpc(
    issueToken ? 'claim_apple_purchase_account_token' : 'get_apple_purchase_eligibility'
  );
  if (error) throw new Error('Apple purchase eligibility could not be verified.');
  const result = firstRow(data);
  if (typeof result?.eligible !== 'boolean' || !result?.reason) {
    throw new Error('Apple purchase eligibility returned an incomplete response.');
  }
  if (issueToken && result.eligible && !result.app_account_token) {
    throw new Error('Apple purchase eligibility returned an incomplete response.');
  }
  return result;
}

export async function getAppleSubscriptionStatus(client) {
  const resolvedClient = await clientOrThrow(client);
  const {data, error} = await resolvedClient.rpc('get_apple_subscription_status');
  if (error) throw new Error('Apple subscription status could not be verified.');
  return firstRow(data) || null;
}

export async function exchangeAppleTransaction(signedTransaction, client) {
  if (typeof signedTransaction !== 'string' || !signedTransaction.trim()) {
    throw new Error('A signed Apple transaction is required.');
  }
  const resolvedClient = await clientOrThrow(client);
  const {data, error} = await resolvedClient.functions.invoke('apple-transaction-exchange', {
    body: {signed_transaction: signedTransaction.trim()}
  });
  if (error || data?.accepted !== true || !data?.transaction_id) {
    throw new Error('The Apple transaction could not be verified by SilaTrail.');
  }
  return data;
}

function requireEnabled(enabled) {
  if (!isAppleIapEnabled(enabled)) {
    throw new Error('Apple purchases are not available in this build.');
  }
}

export async function acceptAppleTransaction(transaction, {client, native = nativeDefaults()} = {}) {
  if (transaction?.verification !== 'verified'
    || typeof transaction.signedTransaction !== 'string'
    || !transaction.signedTransaction
    || !/^\d+$/.test(String(transaction.transactionId || ''))) {
    throw new Error('Apple returned an incomplete verified transaction.');
  }
  const accepted = await exchangeAppleTransaction(transaction.signedTransaction, client);
  if (String(accepted.transaction_id) !== String(transaction.transactionId)) {
    throw new Error('The verified Apple transaction did not match the purchase.');
  }
  const finished = await native.finish(String(transaction.transactionId));
  if (finished?.finished !== true) {
    throw new Error('The verified Apple transaction could not be finished.');
  }
  return accepted;
}

function nativeDefaults() {
  return {
    purchase: (values) => purchaseAppleProduct(values),
    sync: () => syncApplePurchases(),
    currentEntitlements: () => getAppleCurrentEntitlements(),
    finish: (transactionId) => finishAppleTransaction(transactionId)
  };
}

export async function completeApplePurchase(productId, dependencies = {}) {
  requireEnabled(dependencies.enabled ?? enabledValue());
  const client = dependencies.client;
  const native = dependencies.native ?? nativeDefaults();
  const eligibility = await getApplePurchaseEligibility(client, {issueToken: true});
  if (!eligibility.eligible) {
    return {state: 'ineligible', reason: eligibility.reason};
  }

  const result = await native.purchase({
    productId,
    appAccountToken: eligibility.app_account_token
  });
  if (result?.state === 'cancelled' || result?.state === 'pending') {
    return {state: result.state};
  }
  if (result?.state !== 'verified') {
    throw new Error('Apple returned an unsupported purchase state.');
  }
  const entitlement = await acceptAppleTransaction(result.transaction, {client, native});
  return {state: 'verified', entitlement};
}

export async function restoreApplePurchases(dependencies = {}) {
  requireEnabled(dependencies.enabled ?? enabledValue());
  const client = dependencies.client;
  const native = dependencies.native ?? nativeDefaults();
  await native.sync();
  const result = await native.currentEntitlements();
  const transactions = Array.isArray(result?.transactions) ? result.transactions : [];
  const entitlements = [];
  for (const transaction of transactions) {
    entitlements.push(await acceptAppleTransaction(transaction, {client, native}));
  }
  return {restored: entitlements.length, entitlements};
}
